import React, {MouseEvent} from "react";
import {useSelector} from "react-redux";
import {css, Theme} from "@emotion/react";

import {elevation} from "@pg-design/elevation";
import {borderRadius, calculateRemSize, m, mb, mh, onDesktop, p, pb} from "@pg-design/helpers-css";
import {getTextVariantStyleFor} from "@pg-design/text";
import {Text} from "@pg-design/text";
import {useIsMounted} from "@pg-mono/hooks";

import {IRPStore} from "../../../app/rp_reducer";
import {HomepageSearch} from "../../../homepage/components/HomepageSearch";
import {ArticleCategory} from "../../actions/ArticleCategory";
import {hitClickOnArticleLink} from "../../tracking/article_events";
import {ArticleOfferSlider} from "./ArticleOfferSlider";

const quoteBg = require("../../images/quotemark.svg");
const lightBulb = require("../../images/light_bulb.svg");

interface IProps {
    text: string;
    lead: string | null;
    className?: string;
}

const BlogsSearch = () => (
    <div className="blogSearch" css={blogSearchContainer}>
        <Text as="p" variant="headline_4" align="center" css={[pb(3)]}>
            Szukaj nieruchomości
        </Text>
        <HomepageSearch containerHeight="204" blendTopPosition="144" disableMultiRegion autoFocus={false} />
    </div>
);

export const ArticleText: React.FunctionComponent<IProps> = (props) => {
    const article = useSelector((state: IRPStore) => state.article.detail.article);
    const isVideo = article?.categories.find((category) => category?.id === ArticleCategory.Interviews);

    const onContentClick = (e: MouseEvent) => {
        const eventTrigger = e.target as HTMLElement;

        const isTextLink = eventTrigger.closest("a") && !eventTrigger.closest(".custom-add-text-box__container");
        if (article && isTextLink) {
            const textLink = eventTrigger as HTMLAnchorElement;

            hitClickOnArticleLink({
                type: "href",
                link_url: textLink.href,
                article_id: article.id
            });
        }

        const isButtonLink = eventTrigger.closest(".custom-add-text-box__container");
        if (article && isButtonLink) {
            const buttonLink = eventTrigger as HTMLAnchorElement;

            hitClickOnArticleLink({
                type: "button",
                link_url: buttonLink.href,
                article_id: article.id
            });
        }
    };

    return (
        <div className={props.className} css={wrapper}>
            {props.lead && !isVideo ? <div css={articleHeadline} dangerouslySetInnerHTML={{__html: props.lead}} /> : null}
            <MappedText text={props.text} onClick={onContentClick} lead={props.lead} />
        </div>
    );
};

const MappedText = ({text, onClick, lead}: {text: string; lead: string | null; onClick: (e: React.MouseEvent) => void}) => {
    const isMounted = useIsMounted();

    // unify first render on server and client to avoid hydration errors
    if (!isMounted) {
        return <div css={[articleTypography, widgetStyles]} dangerouslySetInnerHTML={{__html: text}} onClick={onClick} />;
    }

    const offerPanelRegex = /<div\s+class="article-offer-panel-holder"(.*?)>.*?<\/div>/g;

    const searchBarRegex = /<div\s+class="rp-article-app" data-apptype="searchBar"(.*?)><\/div>/g;

    const regex = new RegExp(`(?:${offerPanelRegex.source}|${searchBarRegex.source})`, "g");
    const parts = [];
    let lastIndex = 0;
    let match;

    while ((match = regex.exec(text)) !== null) {
        if (match.index > lastIndex) {
            parts.push(text.slice(lastIndex, match.index));
        }
        parts.push(match[0]);
        lastIndex = regex.lastIndex;
    }

    if (lastIndex < text.length) {
        parts.push(text.slice(lastIndex));
    }

    return (
        <div css={[articleTypography, widgetStyles]}>
            {parts.map((part, index) => {
                const dataUrl = part.match(/data-url="([^"]*)"/);
                const searchBars = part.match(/data-apptype="([^"]*)"/);

                const key = `${lead}${index}`;

                if (searchBars?.[1]) {
                    return <BlogsSearch key={key} />;
                }

                if (dataUrl?.[1]) {
                    return <ArticleOfferSlider key={key} dataUrl={dataUrl[1]} />;
                }

                if (!dataUrl && !searchBars) {
                    return <div key={key} dangerouslySetInnerHTML={{__html: part}} onClick={onClick} />;
                }
            })}
        </div>
    );
};

/*
    Styles
 */
const blogSearchContainer = (theme: Theme) => css`
    text-align: center;
    background-color: ${theme.colors.success};
    ${borderRadius(0)};
    ${p(4, 0)};
    margin: 2.4rem -2.4rem;
    border-radius: 0;

    ${onDesktop(p(4), borderRadius(2), m(0, 0, 3, 0))};
`;
const articleHeadline = (theme: Theme) => css`
    ${getTextVariantStyleFor("headline_5")(theme)};

    ${p(2, 1.5, 2, 1.5)};

    ${onDesktop(p(0, 1.5, 2, 1.5))};
`;

const textVerticalSpace = css`
    margin: 0;
    ${m(2, 0, 2, 0)};

    ${onDesktop(m(3, 0, 3, 0))}
`;

const widgetBtnStyle = (theme: Theme) => css`
    -webkit-appearance: none !important;
    display: inline-flex;
    justify-content: center;
    align-items: center;
    touch-action: manipulation;
    background-color: ${theme.colors.primary};

    padding: 0.9rem 1.8rem;
    border-width: 1px;
    border-style: solid;
    border-radius: 50px;
    border-color: ${theme.colors.primary};
    min-height: 4.2rem;
    max-width: 100%;
    color: ${theme.colors.secondary} !important;
    text-transform: uppercase;
    font-weight: 600;
    font-size: 1.575rem;
    line-height: 2.4rem;
    text-align: center;
    transition: box-shadow 0.2s ease-in-out;
    user-select: none;
    text-decoration: none;

    &:hover,
    &:focus,
    &:active,
    &:active:hover,
    &:active:focus,
    &:active.focus {
        outline: 0 !important;
        text-decoration: none;
        background-color: ${theme.colors.primary};
        border-color: ${theme.colors.primary};
        ${elevation(4)};
    }
`;

const widgetStyles = (theme: Theme) => css`
    span[data-role="widget"] {
        display: flex;
        justify-content: center;
        margin: 3.5rem 0;

        a {
            ${widgetBtnStyle(theme)};
            text-decoration: none;
        }
    }

    span[data-widget-type="button-link"] {
        a {
            background-color: ${theme.colors.primary};
            border-color: ${theme.colors.primary};
            color: #fff;
            text-decoration: none;

            &:hover,
            &:focus,
            &:active,
            &:active:hover,
            &:active:focus,
            &:active.focus {
                background-color: ${theme.colors.primary};
                border-color: ${theme.colors.primary};
            }
        }
    }

    span[data-widget-type="text-box"] {
        flex-direction: column;
        align-items: center;
        font-size: 2.243rem;
        line-height: 2.8rem;
        text-align: center;
        padding: 2.6rem 1.5rem;
        color: ${theme.colors.secondary};
        background-color: ${theme.colors.background[200]};
        border-radius: 0.4rem;

        a {
            margin-top: 2.6rem;
            background-color: ${theme.colors.primary};
            border-color: ${theme.colors.primary};
            color: ${theme.colors.secondary};
            text-decoration: none;

            &:hover,
            &:focus,
            &:active,
            &:active:hover,
            &:active:focus,
            &:active.focus {
                background-color: ${theme.colors.primary};
                border-color: ${theme.colors.primary};
            }
        }
    }

    .custom-add-text-box__container {
        display: flex;
        flex-direction: column;
        align-items: center;
        font-size: 2.243rem;
        line-height: 2.8rem;
        font-weight: 500;
        text-align: center;

        color: ${theme.colors.secondary};
        background-color: ${theme.colors.background[200]};
        ${borderRadius(2)};
        ${p(4)};

        ${onDesktop(m(3, 0, 5, 0))}
        &:hover,
        &:focus,
        &:active,
        &:active:hover,
        &:active:focus,
        &:active.focus {
            text-decoration: none;
        }

        .custom-add-text-box__header {
            ${getTextVariantStyleFor("headline_4", true)(theme)}
            font-weight: 500;
            padding: 0;
            margin: 0;
        }

        .custom-add-text-box__btn {
            ${widgetBtnStyle(theme)};
            margin-top: 4rem;
            margin-bottom: 0;
            min-width: 20rem;
            background-color: ${theme.colors.primary};
            border-color: ${theme.colors.primary};
            color: ${theme.colors.secondary};
        }
    }

    .video-wrapper {
        position: relative;
        overflow: hidden;
        width: 100%;
        padding-top: 75%;

        @media (min-width: ${theme.breakpoints.md}) {
            padding-top: 56.25%;
        }
    }

    .video-style {
        position: absolute;
        top: 0;
        left: 0;
        bottom: 0;
        right: 0;
        width: 100%;
        height: 100%;
    }
`;

const articleTypography = (theme: Theme) => css`
    ${mh(1.5)};

    h1,
    h2:not(.article-offer-slider h2),
    h3,
    h4,
    h5,
    h6 {
        ${textVerticalSpace};

        span:not(.article-offer-slider span) {
            font-weight: inherit !important;
        }
    }

    h1 {
        ${getTextVariantStyleFor("headline_1")(theme)};
    }

    h2:not(.article-offer-slider h2) {
        ${getTextVariantStyleFor("headline_2")(theme)};
    }

    h3 {
        ${getTextVariantStyleFor("headline_3")(theme)};
    }

    h4 {
        ${getTextVariantStyleFor("headline_4")(theme)};
    }

    h5 {
        ${getTextVariantStyleFor("headline_5")(theme)};
    }

    h6 {
        ${getTextVariantStyleFor("headline_6")(theme)};
    }

    ul:not(.blogSearch ul),
    ol:not(.blogSearch ol) {
        ${getTextVariantStyleFor("body_copy_0")(theme)};
        margin: 0;

        ${m(0, 0, 2, 3)}
        ${onDesktop(m(0, 0, 4, 3))}
        em {
            word-break: break-word;
        }
    }

    figure:has(img) {
        width: 100%;
        padding: 0;
        margin: 0;
    }

    img {
        height: auto;
        max-width: 100%;
    }

    div:has(table) {
        overflow-x: auto;
    }

    //Overwriting TinyMCE default margins on floated img's
    img[style*="float: left"] {
        margin: 1rem !important;
        margin-left: 0 !important;
    }

    img[style*="float: right"] {
        margin: 1rem !important;
        margin-right: 0 !important;
    }

    table {
        margin: 2rem 0;
        border: none;
        border-color: #f2f2f2 !important;
        ${textVerticalSpace};
        ${getTextVariantStyleFor("body_copy_0")(theme)};

        td {
            padding: 0.5rem;

            p {
                margin: 0;
            }
        }
    }

    p:not(.article-offer-slider p):not(.blogSearch p),
    span:not(.article-offer-slider span) {
        ${textVerticalSpace};
        ${getTextVariantStyleFor("body_copy_0")(theme)};
    }

    blockquote {
        position: relative;
        color: ${theme.colors.gray[800]};
        border: none;
        font-style: italic;
        background-size: 6.4rem;
        background-position: 8px;
        background-color: #fff;
        ${borderRadius(2)};
        ${p(2, 2, 2, 7)};
        ${m(4, 0, 4, 0)};

        font-size: 1.35rem;
        line-height: 2rem;

        ${onDesktop(
            p(2, 2, 2, 11),
            m(4, 0, 9, 0),
            css`
                line-height: 2.4rem;
                font-size: 1.575rem;
            `
        )}
        &:before {
            content: "";
            position: absolute;
            background-image: url(${quoteBg});
            background-position: center;
            background-size: contain;
            top: 8px;
            left: 8px;
            height: 3.2rem;
            width: 3.2rem;
            z-index: 2;

            ${onDesktop(css`
                height: 6.4rem;
                width: 6.4rem;
            `)};
        }
    }

    .light-bulb-block {
        position: relative;
        color: ${theme.colors.gray[800]};
        border: none;
        font-style: italic;
        background-size: 6.4rem;
        background-position: 8px;
        background-color: #fff;
        text-align: center;
        ${borderRadius(2)};
        ${p(8, 4, 4, 4)};
        ${m(4, 0, 4, 0)};

        font-size: 1.519rem;
        line-height: 2rem;

        ${onDesktop(
            p(14, 4, 4, 4),
            m(4, 0, 9, 0),
            css`
                line-height: 2.4rem;
                font-size: 1.575rem;
            `
        )}

        &:before {
            content: "";
            position: absolute;
            background-image: url(${lightBulb});
            background-position: center;
            background-size: contain;
            top: 1.6rem;
            left: 50%;
            transform: translateX(-50%);
            height: 3.2rem;
            width: 3.2rem;
            z-index: 2;

            ${onDesktop(css`
                top: 2.4rem;
                height: 6.4rem;
                width: 6.4rem;
            `)};
        }
    }

    iframe {
        max-width: 100%;
    }

    //underscore text for SEO, except widgets
    a:not(.custom-add-text-box__container) {
        text-decoration: underline;
        text-decoration-color: #ebff00;
        text-decoration-thickness: 5px;
        text-underline-offset: 0.1em;
        text-decoration-skip-ink: none;
    }

    .article-offer-slider a,
    a[data-testid="results-list-item"] {
        text-decoration: none;
    }
`;

const wrapper = (theme: Theme) => css`
    ${mb(4)};
    max-width: calc(100vw - ${calculateRemSize(3)});
    @media (min-width: ${theme.breakpoints.md}) {
        max-width: initial;
    }
`;
import React, {MouseEvent} from "react";
import {useSelector} from "react-redux";
import {css, Theme} from "@emotion/react";

import {elevation} from "@pg-design/elevation";
import {borderRadius, calculateRemSize, m, mb, mh, onDesktop, p, pb} from "@pg-design/helpers-css";
import {getTextVariantStyleFor} from "@pg-design/text";
import {Text} from "@pg-design/text";
import {useIsMounted} from "@pg-mono/hooks";

import {IRPStore} from "../../../app/rp_reducer";
import {HomepageSearch} from "../../../homepage/components/HomepageSearch";
import {ArticleCategory} from "../../actions/ArticleCategory";
import {hitClickOnArticleLink} from "../../tracking/article_events";
import {ArticleOfferSlider} from "./ArticleOfferSlider";

const quoteBg = require("../../images/quotemark.svg");
const lightBulb = require("../../images/light_bulb.svg");

interface IProps {
    text: string;
    lead: string | null;
    className?: string;
}

const BlogsSearch = () => (
    <div className="blogSearch" css={blogSearchContainer}>
        <Text as="p" variant="headline_4" align="center" css={[pb(3)]}>
            Szukaj nieruchomości
        </Text>
        <HomepageSearch containerHeight="204" blendTopPosition="144" disableMultiRegion autoFocus={false} />
    </div>
);

export const ArticleText: React.FunctionComponent<IProps> = (props) => {
    const article = useSelector((state: IRPStore) => state.article.detail.article);
    const isVideo = article?.categories.find((category) => category?.id === ArticleCategory.Interviews);

    const onContentClick = (e: MouseEvent) => {
        const eventTrigger = e.target as HTMLElement;

        const isTextLink = eventTrigger.closest("a") && !eventTrigger.closest(".custom-add-text-box__container");
        if (article && isTextLink) {
            const textLink = eventTrigger as HTMLAnchorElement;

            hitClickOnArticleLink({
                type: "href",
                link_url: textLink.href,
                article_id: article.id
            });
        }

        const isButtonLink = eventTrigger.closest(".custom-add-text-box__container");
        if (article && isButtonLink) {
            const buttonLink = eventTrigger as HTMLAnchorElement;

            hitClickOnArticleLink({
                type: "button",
                link_url: buttonLink.href,
                article_id: article.id
            });
        }
    };

    return (
        <div className={props.className} css={wrapper}>
            {props.lead && !isVideo ? <div css={articleHeadline} dangerouslySetInnerHTML={{__html: props.lead}} /> : null}
            <MappedText text={props.text} onClick={onContentClick} lead={props.lead} />
        </div>
    );
};

const MappedText = ({text, onClick, lead}: {text: string; lead: string | null; onClick: (e: React.MouseEvent) => void}) => {
    const isMounted = useIsMounted();

    // unify first render on server and client to avoid hydration errors
    if (!isMounted) {
        return <div css={[articleTypography, widgetStyles]} dangerouslySetInnerHTML={{__html: text}} onClick={onClick} />;
    }

    const offerPanelRegex = /<div\s+class="article-offer-panel-holder"(.*?)>.*?<\/div>/g;

    const searchBarRegex = /<div\s+class="rp-article-app" data-apptype="searchBar"(.*?)><\/div>/g;

    const regex = new RegExp(`(?:${offerPanelRegex.source}|${searchBarRegex.source})`, "g");
    const parts = [];
    let lastIndex = 0;
    let match;

    while ((match = regex.exec(text)) !== null) {
        if (match.index > lastIndex) {
            parts.push(text.slice(lastIndex, match.index));
        }
        parts.push(match[0]);
        lastIndex = regex.lastIndex;
    }

    if (lastIndex < text.length) {
        parts.push(text.slice(lastIndex));
    }

    return (
        <div css={[articleTypography, widgetStyles]}>
            {parts.map((part, index) => {
                const dataUrl = part.match(/data-url="([^"]*)"/);
                const searchBars = part.match(/data-apptype="([^"]*)"/);

                const key = `${lead}${index}`;

                if (searchBars?.[1]) {
                    return <BlogsSearch key={key} />;
                }

                if (dataUrl?.[1]) {
                    return <ArticleOfferSlider key={key} dataUrl={dataUrl[1]} />;
                }

                if (!dataUrl && !searchBars) {
                    return <div key={key} dangerouslySetInnerHTML={{__html: part}} onClick={onClick} />;
                }
            })}
        </div>
    );
};

/*
    Styles
 */
const blogSearchContainer = (theme: Theme) => css`
    text-align: center;
    background-color: ${theme.colors.success};
    ${borderRadius(0)};
    ${p(4, 0)};
    margin: 2.4rem -2.4rem;
    border-radius: 0;

    ${onDesktop(p(4), borderRadius(2), m(0, 0, 3, 0))};
`;
const articleHeadline = (theme: Theme) => css`
    ${getTextVariantStyleFor("headline_5")(theme)};

    ${p(2, 1.5, 2, 1.5)};

    ${onDesktop(p(0, 1.5, 2, 1.5))};
`;

const textVerticalSpace = css`
    margin: 0;
    ${m(2, 0, 2, 0)};

    ${onDesktop(m(3, 0, 3, 0))}
`;

const widgetBtnStyle = (theme: Theme) => css`
    -webkit-appearance: none !important;
    display: inline-flex;
    justify-content: center;
    align-items: center;
    touch-action: manipulation;
    background-color: ${theme.colors.primary};

    padding: 0.9rem 1.8rem;
    border-width: 1px;
    border-style: solid;
    border-radius: 50px;
    border-color: ${theme.colors.primary};
    min-height: 4.2rem;
    max-width: 100%;
    color: ${theme.colors.secondary} !important;
    text-transform: uppercase;
    font-weight: 600;
    font-size: 1.575rem;
    line-height: 2.4rem;
    text-align: center;
    transition: box-shadow 0.2s ease-in-out;
    user-select: none;
    text-decoration: none;

    &:hover,
    &:focus,
    &:active,
    &:active:hover,
    &:active:focus,
    &:active.focus {
        outline: 0 !important;
        text-decoration: none;
        background-color: ${theme.colors.primary};
        border-color: ${theme.colors.primary};
        ${elevation(4)};
    }
`;

const widgetStyles = (theme: Theme) => css`
    span[data-role="widget"] {
        display: flex;
        justify-content: center;
        margin: 3.5rem 0;

        a {
            ${widgetBtnStyle(theme)};
            text-decoration: none;
        }
    }

    span[data-widget-type="button-link"] {
        a {
            background-color: ${theme.colors.primary};
            border-color: ${theme.colors.primary};
            color: #fff;
            text-decoration: none;

            &:hover,
            &:focus,
            &:active,
            &:active:hover,
            &:active:focus,
            &:active.focus {
                background-color: ${theme.colors.primary};
                border-color: ${theme.colors.primary};
            }
        }
    }

    span[data-widget-type="text-box"] {
        flex-direction: column;
        align-items: center;
        font-size: 2.243rem;
        line-height: 2.8rem;
        text-align: center;
        padding: 2.6rem 1.5rem;
        color: ${theme.colors.secondary};
        background-color: ${theme.colors.background[200]};
        border-radius: 0.4rem;

        a {
            margin-top: 2.6rem;
            background-color: ${theme.colors.primary};
            border-color: ${theme.colors.primary};
            color: ${theme.colors.secondary};
            text-decoration: none;

            &:hover,
            &:focus,
            &:active,
            &:active:hover,
            &:active:focus,
            &:active.focus {
                background-color: ${theme.colors.primary};
                border-color: ${theme.colors.primary};
            }
        }
    }

    .custom-add-text-box__container {
        display: flex;
        flex-direction: column;
        align-items: center;
        font-size: 2.243rem;
        line-height: 2.8rem;
        font-weight: 500;
        text-align: center;

        color: ${theme.colors.secondary};
        background-color: ${theme.colors.background[200]};
        ${borderRadius(2)};
        ${p(4)};

        ${onDesktop(m(3, 0, 5, 0))}
        &:hover,
        &:focus,
        &:active,
        &:active:hover,
        &:active:focus,
        &:active.focus {
            text-decoration: none;
        }

        .custom-add-text-box__header {
            ${getTextVariantStyleFor("headline_4", true)(theme)}
            font-weight: 500;
            padding: 0;
            margin: 0;
        }

        .custom-add-text-box__btn {
            ${widgetBtnStyle(theme)};
            margin-top: 4rem;
            margin-bottom: 0;
            min-width: 20rem;
            background-color: ${theme.colors.primary};
            border-color: ${theme.colors.primary};
            color: ${theme.colors.secondary};
        }
    }

    .video-wrapper {
        position: relative;
        overflow: hidden;
        width: 100%;
        padding-top: 75%;

        @media (min-width: ${theme.breakpoints.md}) {
            padding-top: 56.25%;
        }
    }

    .video-style {
        position: absolute;
        top: 0;
        left: 0;
        bottom: 0;
        right: 0;
        width: 100%;
        height: 100%;
    }
`;

const articleTypography = (theme: Theme) => css`
    ${mh(1.5)};

    h1,
    h2:not(.article-offer-slider h2),
    h3,
    h4,
    h5,
    h6 {
        ${textVerticalSpace};

        span:not(.article-offer-slider span) {
            font-weight: inherit !important;
        }
    }

    h1 {
        ${getTextVariantStyleFor("headline_1")(theme)};
    }

    h2:not(.article-offer-slider h2) {
        ${getTextVariantStyleFor("headline_2")(theme)};
    }

    h3 {
        ${getTextVariantStyleFor("headline_3")(theme)};
    }

    h4 {
        ${getTextVariantStyleFor("headline_4")(theme)};
    }

    h5 {
        ${getTextVariantStyleFor("headline_5")(theme)};
    }

    h6 {
        ${getTextVariantStyleFor("headline_6")(theme)};
    }

    ul:not(.blogSearch ul),
    ol:not(.blogSearch ol) {
        ${getTextVariantStyleFor("body_copy_0")(theme)};
        margin: 0;

        ${m(0, 0, 2, 3)}
        ${onDesktop(m(0, 0, 4, 3))}
        em {
            word-break: break-word;
        }
    }

    figure:has(img) {
        width: 100%;
        padding: 0;
        margin: 0;
    }

    img {
        height: auto;
        max-width: 100%;
    }

    div:has(table) {
        overflow-x: auto;
    }

    //Overwriting TinyMCE default margins on floated img's
    img[style*="float: left"] {
        margin: 1rem !important;
        margin-left: 0 !important;
    }

    img[style*="float: right"] {
        margin: 1rem !important;
        margin-right: 0 !important;
    }

    table {
        margin: 2rem 0;
        border: none;
        border-color: #f2f2f2 !important;
        ${textVerticalSpace};
        ${getTextVariantStyleFor("body_copy_0")(theme)};

        td {
            padding: 0.5rem;

            p {
                margin: 0;
            }
        }
    }

    p:not(.article-offer-slider p):not(.blogSearch p),
    span:not(.article-offer-slider span) {
        ${textVerticalSpace};
        ${getTextVariantStyleFor("body_copy_0")(theme)};
    }

    blockquote {
        position: relative;
        color: ${theme.colors.gray[800]};
        border: none;
        font-style: italic;
        background-size: 6.4rem;
        background-position: 8px;
        background-color: #fff;
        ${borderRadius(2)};
        ${p(2, 2, 2, 7)};
        ${m(4, 0, 4, 0)};

        font-size: 1.35rem;
        line-height: 2rem;

        ${onDesktop(
            p(2, 2, 2, 11),
            m(4, 0, 9, 0),
            css`
                line-height: 2.4rem;
                font-size: 1.575rem;
            `
        )}
        &:before {
            content: "";
            position: absolute;
            background-image: url(${quoteBg});
            background-position: center;
            background-size: contain;
            top: 8px;
            left: 8px;
            height: 3.2rem;
            width: 3.2rem;
            z-index: 2;

            ${onDesktop(css`
                height: 6.4rem;
                width: 6.4rem;
            `)};
        }
    }

    .light-bulb-block {
        position: relative;
        color: ${theme.colors.gray[800]};
        border: none;
        font-style: italic;
        background-size: 6.4rem;
        background-position: 8px;
        background-color: #fff;
        text-align: center;
        ${borderRadius(2)};
        ${p(8, 4, 4, 4)};
        ${m(4, 0, 4, 0)};

        font-size: 1.519rem;
        line-height: 2rem;

        ${onDesktop(
            p(14, 4, 4, 4),
            m(4, 0, 9, 0),
            css`
                line-height: 2.4rem;
                font-size: 1.575rem;
            `
        )}

        &:before {
            content: "";
            position: absolute;
            background-image: url(${lightBulb});
            background-position: center;
            background-size: contain;
            top: 1.6rem;
            left: 50%;
            transform: translateX(-50%);
            height: 3.2rem;
            width: 3.2rem;
            z-index: 2;

            ${onDesktop(css`
                top: 2.4rem;
                height: 6.4rem;
                width: 6.4rem;
            `)};
        }
    }

    iframe {
        max-width: 100%;
    }

    //underscore text for SEO, except widgets
    a:not(.custom-add-text-box__container) {
        text-decoration: underline;
        text-decoration-color: #ebff00;
        text-decoration-thickness: 5px;
        text-underline-offset: 0.1em;
        text-decoration-skip-ink: none;
    }

    .article-offer-slider a,
    a[data-testid="results-list-item"] {
        text-decoration: none;
    }
`;

const wrapper = (theme: Theme) => css`
    ${mb(4)};
    max-width: calc(100vw - ${calculateRemSize(3)});
    @media (min-width: ${theme.breakpoints.md}) {
        max-width: initial;
    }
`;
